import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth';
import { usePair } from '../hooks/usePair';
import { useModuleProgress } from '../hooks/useModuleProgress';
import { useMeetings } from '../hooks/useMeetings';
import { usePrayerRequests } from '../hooks/usePrayerRequests';
import { updateProfile } from '../supabase/api';
import LinkParticipantPanel from '../components/LinkParticipantPanel';

const {
  FiUser, FiMail, FiPhone, FiMapPin, FiCalendar,
  FiEdit3, FiSave, FiX, FiUsers, FiHeart, FiBook,
  FiTarget, FiLoader, FiCheckCircle, FiClock, FiChevronRight,
} = FiIcons;

const WEEK_TITLES = {
  1: 'Jesus is Your Salvation and Story',
  2: 'You Are A New Creation',
  3: 'The Holy Spirit Points You To Jesus',
  4: 'Surrender Your Struggles',
  5: 'You Are Called To Give Generously',
  6: 'Follow Jesus By Serving Others',
};

const WEEK_SCRIPTURES = {
  1: 'John 3:16-19',
  2: 'Galatians 2:20',
  3: 'John 16:13-14',
  4: '1 Corinthians 10:13',
  5: '2 Corinthians 9:6-7',
  6: 'Matthew 5:16',
};

function Field({ icon, label, type: inputType = 'text', placeholder, value, onChange, isEditable }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      <div className="relative">
        <SafeIcon icon={icon} className="absolute left-3 top-3 w-5 h-5 text-gray-400 dark:text-gray-500" />
        {isEditable ? (
          <input
            type={inputType}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg custom-focus transition-colors"
          />
        ) : (
          <div className="pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white transition-colors">{value || 'Not set'}</div>
        )}
      </div>
    </div>
  );
}

function ProfileCard({ profile, formData, isEditable, onChange }) {
  const isLeader = profile?.role === 'leader';
  const update = (field) => (e) => onChange({ ...formData, [field]: e.target.value });

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
      <div className="flex items-center space-x-6 mb-6">
        <div className={`w-20 h-20 ${isLeader ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-green-100 dark:bg-green-900/30'} rounded-full flex items-center justify-center transition-colors`}>
          <SafeIcon icon={FiUser} className={`w-10 h-10 ${isLeader ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`} />
        </div>
        <div className="flex-1">
          {isEditable ? (
            <input
              type="text"
              value={formData.name || ''}
              onChange={update('name')}
              placeholder="Enter name"
              className="text-2xl font-space font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-primary focus:outline-none w-full transition-colors"
            />
          ) : (
            <h2 className="text-2xl font-space font-bold text-gray-900 dark:text-white">{formData.name || 'Not set'}</h2>
          )}
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 transition-colors ${
            isLeader ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' : 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
          }`}>
            {isLeader ? 'Discipleship Leader' : 'Participant'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field icon={FiMail} label="Email Address" type="email" placeholder="email@example.com" value={formData.email} onChange={update('email')} isEditable={isEditable} />
        <Field icon={FiPhone} label="Phone Number" type="tel" placeholder="+1 (555) 123-4567" value={formData.phone} onChange={update('phone')} isEditable={isEditable} />
        <Field icon={FiMapPin} label="Location" placeholder="City, State" value={formData.location} onChange={update('location')} isEditable={isEditable} />
        <Field icon={FiUsers} label="Church" placeholder="Church name" value={formData.church} onChange={update('church')} isEditable={isEditable} />
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio</label>
        {isEditable ? (
          <textarea
            value={formData.bio || ''}
            onChange={update('bio')}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg custom-focus transition-colors"
            placeholder="Tell us about your faith journey..."
          />
        ) : (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white transition-colors">{formData.bio || 'Not set'}</div>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm transition-colors">
        <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
          <SafeIcon icon={FiCalendar} className="w-4 h-4" />
          <span>
            Member since{' '}
            {formData.join_date
              ? new Date(formData.join_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
              : 'Unknown'}
          </span>
        </div>
        <div className="text-gray-600 dark:text-gray-400">{formData.experience || 'Not set'}</div>
      </div>
    </motion.div>
  );
}

function WeeklySummaries({ progress, progressLoading }) {
  const completed = progress.filter((p) => p.status === 'completed');
  const inProgress = progress.filter((p) => p.status === 'in_progress');

  if (progressLoading) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto animate-spin mb-3" />
        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading progress…</p>
      </div>
    );
  }

  if (completed.length === 0 && inProgress.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiBook} className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Weekly Summaries Yet</h4>
        <p className="text-gray-600 dark:text-gray-400 mb-6">Complete your first week to see summaries of your progress.</p>
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/50 text-left transition-colors">
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiCalendar} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Ready to Start?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Begin with Week 1: Jesus is Your Salvation and Story</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const allActive = [...completed, ...inProgress].sort((a, b) => a.week_number - b.week_number);

  return (
    <div className="space-y-3">
      {allActive.map((week) => {
        const isCompleted = week.status === 'completed';
        return (
          <Link
            key={week.id}
            to={`/week/${week.week_number}`}
            className="flex items-center justify-between p-4 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-primary dark:hover:border-primary hover:shadow-sm transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                <SafeIcon
                  icon={isCompleted ? FiCheckCircle : FiClock}
                  className={`w-5 h-5 ${isCompleted ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}
                />
              </div>
              <div>
                <p className={`font-medium group-hover:text-primary transition-colors ${
                  isCompleted ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'
                }`}>
                  Week {week.week_number}: {WEEK_TITLES[week.week_number]}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{WEEK_SCRIPTURES[week.week_number]}</p>
                {isCompleted && week.completed_at && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                    Completed {new Date(week.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isCompleted ? (
                <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-2 py-1 rounded-full font-medium transition-colors">Done ✓</span>
              ) : (
                <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full font-medium transition-colors">In Progress</span>
              )}
              <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-primary" />
            </div>
          </Link>
        );
      })}

      {completed.length > 0 && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/50 rounded-xl flex items-center gap-3 transition-colors">
          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-300 font-medium">
            {completed.length} of 6 weeks completed — {Math.round((completed.length / 6) * 100)}% of your journey done!
          </p>
        </div>
      )}
    </div>
  );
}

function Profile({ userRole, setUserRole }) {
  const { user, profile, refetchProfile } = useAuth();
  const { pair, loading: pairLoading, refetch: refetchPair } = usePair(user?.id, userRole);
  const { progress, completedCount, loading: progressLoading } = useModuleProgress(pair?.id);
  const { meetings } = useMeetings(pair?.id, user?.id);
  const { prayers } = usePrayerRequests(pair?.id, user?.id);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const [localPair, setLocalPair] = useState(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        location: profile.location || '',
        church: profile.church || '',
        bio: profile.bio || '',
        experience: profile.experience || '',
        join_date: profile.join_date || new Date().toISOString().split('T')[0],
      });
    }
  }, [profile]);

  useEffect(() => {
    setLocalPair(pair);
  }, [pair]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      await updateProfile(user.id, formData);
      await refetchProfile();
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        location: profile.location || '',
        church: profile.church || '',
        bio: profile.bio || '',
        experience: profile.experience || '',
        join_date: profile.join_date || new Date().toISOString().split('T')[0],
      });
    }
    setIsEditing(false);
    setError('');
  };

  const activePrayers = prayers.filter((p) => !p.is_answered).length;

  const stats = [
    { icon: FiBook, label: 'Weeks Completed', value: progressLoading ? '…' : `${completedCount}/6`, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    { icon: FiCalendar, label: 'Meetings Scheduled', value: `${meetings.length}`, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    { icon: FiHeart, label: 'Active Prayers', value: `${activePrayers}`, color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/30' },
    { icon: FiTarget, label: 'Journey Progress', value: progressLoading ? '…' : `${Math.round((completedCount / 6) * 100)}%`, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-colors duration-200">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-space font-bold text-gray-900 dark:text-white mb-2">My Profile</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage your discipleship profile information.</p>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
              <span>Edit Profile</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2 disabled:opacity-60"
              >
                {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
                <span>{saving ? 'Saving…' : 'Save'}</span>
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          )}
        </div>
        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg text-sm text-red-600 dark:text-red-400 transition-colors">
            {error}
          </div>
        )}
      </motion.div>

      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8 transition-colors">
        <h3 className="text-xl font-space font-semibold text-gray-900 dark:text-white mb-6">Discipleship Journey Progress</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className={`w-16 h-16 ${stat.bgColor} rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors`}>
                <SafeIcon icon={stat.icon} className={`w-8 h-8 ${stat.color}`} />
              </div>
              <div className="text-3xl font-space font-bold text-gray-900 dark:text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Profile Card */}
      <div className="mb-6">
        <ProfileCard profile={profile} formData={formData} isEditable={isEditing} onChange={setFormData} />
      </div>

      {/* Link Participant Panel — leaders only */}
      {userRole === 'leader' && user && (
        <div className="mb-6">
          {pairLoading ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 flex items-center gap-3 transition-colors">
              <SafeIcon icon={FiLoader} className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-spin" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading pair info…</span>
            </div>
          ) : (
            <LinkParticipantPanel
              leaderId={user.id}
              pair={localPair}
              onPairUpdate={(updated) => {
                setLocalPair(updated);
                refetchPair();
              }}
            />
          )}
        </div>
      )}

      {/* Weekly Summaries */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-space font-semibold text-gray-900 dark:text-white">Weekly Summaries</h3>
          {!progressLoading && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {completedCount} of 6 weeks completed
            </span>
          )}
        </div>
        <WeeklySummaries progress={progress} progressLoading={progressLoading} />
      </motion.div>
    </div>
  );
}

export default Profile;